import { useEffect, useRef } from "react";

/**
 * Neural Network Constellation — animated tech visual for Hero.
 * Renders nodes connected by lines, with floating motion + glowing core.
 * Lightweight: CSS + SVG only (no canvas, no libs).
 */

// Tag positions on a 100x100 grid (used responsively as %)
const NODES = [
  { id: "ai",       label: "AI",            x: 50, y: 14, ring: 0, color: "#60a5fa" },
  { id: "iot",      label: "IoT",           x: 84, y: 30, ring: 1, color: "#22d3ee" },
  { id: "software", label: "Software",      x: 86, y: 70, ring: 0, color: "#818cf8" },
  { id: "hardware", label: "Hardware",      x: 50, y: 86, ring: 1, color: "#38bdf8" },
  { id: "energy",   label: "Smart Energy",  x: 14, y: 70, ring: 0, color: "#a78bfa" },
  { id: "cloud",    label: "Cloud",         x: 14, y: 30, ring: 1, color: "#7dd3fc" },
];

const CONNECTIONS = [
  ["ai", "iot"], ["iot", "software"], ["software", "hardware"],
  ["hardware", "energy"], ["energy", "cloud"], ["cloud", "ai"],
  ["ai", "software"], ["iot", "hardware"], ["energy", "ai"], ["cloud", "hardware"],
];

const TechOrbit = () => {
  const rootRef = useRef(null);

  // subtle parallax follow on hover
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const cx = (e.clientX - r.left) / r.width - 0.5;
      const cy = (e.clientY - r.top) / r.height - 0.5;
      el.style.setProperty("--tx", `${cx * 8}px`);
      el.style.setProperty("--ty", `${cy * 8}px`);
    };
    const onLeave = () => {
      el.style.setProperty("--tx", `0px`);
      el.style.setProperty("--ty", `0px`);
    };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  const nodeMap = Object.fromEntries(NODES.map((n) => [n.id, n]));

  return (
    <div
      ref={rootRef}
      data-testid="tech-orbit"
      className="relative w-full max-w-[560px] aspect-square mx-auto select-none"
      style={{ transform: "translate3d(var(--tx,0), var(--ty,0), 0)", transition: "transform .2s ease" }}
    >
      {/* glow halos */}
      <div className="absolute inset-12 rounded-full bg-blue-500/12 blur-3xl animate-pulse-soft" />
      <div className="absolute inset-24 rounded-full bg-indigo-500/12 blur-2xl animate-pulse-soft" style={{ animationDelay: "1.5s" }} />
      <div className="absolute inset-32 rounded-full bg-cyan-500/10 blur-2xl animate-pulse-soft" style={{ animationDelay: "3s" }} />

      {/* concentric rings */}
      <div className="absolute inset-0 rounded-full border border-white/[0.05]" />
      <div className="absolute inset-[10%] rounded-full border border-white/[0.06]" />
      <div className="absolute inset-[22%] rounded-full border border-white/[0.07] animate-orbit-slow" style={{ borderStyle: "dashed", borderColor: "rgba(96,165,250,0.18)" }} />
      <div className="absolute inset-[36%] rounded-full border border-blue-400/20" />

      {/* radar sweep */}
      <div className="absolute inset-0 rounded-full overflow-hidden">
        <div
          className="absolute inset-0 animate-orbit-slow"
          style={{
            background: "conic-gradient(from 0deg, transparent 0deg, rgba(96,165,250,0.18) 30deg, transparent 60deg)",
            maskImage: "radial-gradient(circle, transparent 35%, black 36%, black 80%, transparent 81%)",
            WebkitMaskImage: "radial-gradient(circle, transparent 35%, black 36%, black 80%, transparent 81%)",
          }}
        />
      </div>

      {/* SVG: connections + animated data pulses */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" fill="none">
        <defs>
          <linearGradient id="line-grad" x1="0" y1="0" x2="100" y2="100">
            <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.6" />
            <stop offset="50%" stopColor="#22d3ee" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#a78bfa" stopOpacity="0.6" />
          </linearGradient>
          <radialGradient id="node-grad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#fff" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#60a5fa" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* connection lines */}
        {CONNECTIONS.map(([a, b], i) => {
          const A = nodeMap[a];
          const B = nodeMap[b];
          return (
            <line
              key={i}
              x1={A.x}
              y1={A.y}
              x2={B.x}
              y2={B.y}
              stroke="url(#line-grad)"
              strokeWidth="0.18"
              opacity="0.55"
            />
          );
        })}

        {/* animated dashed line */}
        <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(96,165,250,0.35)" strokeWidth="0.15" className="animate-dash" />
        <circle cx="50" cy="50" r="30" fill="none" stroke="rgba(34,211,238,0.3)" strokeWidth="0.15" className="animate-dash" style={{ animationDuration: "9s", animationDirection: "reverse" }} />

        {/* travelling pulses on each connection */}
        {CONNECTIONS.map(([a, b], i) => {
          const A = nodeMap[a];
          const B = nodeMap[b];
          return (
            <circle key={`p-${i}`} r="0.55" fill="#7dd3fc">
              <animateMotion
                dur={`${4 + (i % 4)}s`}
                repeatCount="indefinite"
                begin={`${i * 0.4}s`}
                path={`M ${A.x} ${A.y} L ${B.x} ${B.y}`}
              />
            </circle>
          );
        })}
      </svg>

      {/* Nodes */}
      {NODES.map((n) => (
        <div
          key={n.id}
          className="absolute"
          style={{
            left: `${n.x}%`,
            top: `${n.y}%`,
            transform: "translate(-50%, -50%)",
          }}
        >
          <div className="relative">
            <div
              className="absolute inset-0 rounded-full blur-md animate-pulse-soft"
              style={{ background: n.color, opacity: 0.35 }}
            />
            <div className="relative w-3.5 h-3.5 rounded-full ring-2 ring-white/30 shadow-lg" style={{ background: n.color }} />
          </div>
          <div
            className="mt-2 text-[10px] uppercase tracking-[0.22em] whitespace-nowrap"
            style={{ color: n.color, textShadow: `0 0 14px ${n.color}55` }}
          >
            {n.label}
          </div>
        </div>
      ))}

      {/* Core badge */}
      <div className="absolute inset-0 grid place-items-center">
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-blue-500/30 blur-2xl animate-pulse-soft" />
          <div className="relative w-32 h-32 sm:w-36 sm:h-36 rounded-full glass-strong border border-blue-400/30 grid place-items-center shadow-[0_0_70px_rgba(59,130,246,0.4)]">
            <div className="text-center px-4">
              <div className="text-[9px] uppercase tracking-[0.3em] text-cyan-300/90 mb-1.5 font-mono-display">
                /// CORE
              </div>
              <div className="font-medium text-white text-lg leading-tight" style={{ fontFamily: "Outfit" }}>
                Product
                <br />
                <span className="text-gradient-aurora">· Solution</span>
              </div>
              <div className="mt-1.5 inline-flex items-center gap-1 text-[9px] text-emerald-300/90 font-mono-display">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-blink" />
                ONLINE
              </div>
            </div>
            <span className="absolute inset-0 rounded-full ring-1 ring-white/10" />
            {/* tick marks */}
            {Array.from({ length: 12 }).map((_, i) => (
              <span
                key={i}
                className="absolute left-1/2 top-1/2 origin-bottom"
                style={{
                  width: 1,
                  height: "52%",
                  background: "linear-gradient(180deg, transparent 0%, rgba(148,163,184,0.3) 60%, transparent 100%)",
                  transform: `translate(-50%, -100%) rotate(${i * 30}deg)`,
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechOrbit;
